// Add / edit one track of a session: name, description, sequence, assignment
// type, objectives, instructions and an OPTIONAL audience rule (the shared
// ConditionGroupBuilder + Preview). Emits a TrackInput; the caller decides
// whether that becomes POST /sessions/{id}/tracks, PATCH /tracks/{id}, or a
// wizard draft. Membership is never derived here — a track without a rule is
// the session's default, and the server picks the first matching track.
// Phase 4: optional completion policy / practical exercise bindings that
// override the session's for this track's members (absent = keep, null =
// clear, id = bind).
import { useT } from '../lib/i18n';
import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { ConditionGroupBuilder } from '@/features/shared/components/ConditionGroupBuilder';
import { validateRule, type RuleNode } from '@/features/shared/components/conditionGroup';
import { getApiErrorMessage } from '@/lib/apiError';
import type { AssignmentType, TrackInput } from '@/services/learningService';
import { useAudienceFacets, useAudiencePreview } from '../api/hooks';
import { useRuleVocabulary } from '../lib/audienceRules';
import { AudiencePreviewPanel } from './AudiencePreviewPanel';
import { PolicyPracticeSelects } from './PolicyPracticeSelects';

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

export interface TrackEditorInitial extends Partial<TrackInput> { id?: string }

interface Props {
  /** Existing values (edit) — `rule` present = the track currently has a rule. */
  initial?: TrackEditorInitial | null;
  /** Suggested sequence for a new track (1-based). */
  defaultSequence?: number;
  onSave: (input: TrackInput) => void;
  onCancel: () => void;
  busy?: boolean;
  /** Prefix for element ids (several editors on one page). */
  idPrefix?: string;
  className?: string;
  /** Link to the admin Practice / Assessments tab (shown when the viewer may manage). */
  manageHref?: string | null;
}

export const TrackEditor: React.FC<Props> = ({ initial, defaultSequence, onSave, onCancel, busy, idPrefix = 'te', className, manageHref }) => {
  const t = useT();
  const facetsQ = useAudienceFacets(true);
  const vocab = useRuleVocabulary(t, facetsQ.data);
  const preview = useAudiencePreview();
  const hadRule = !!initial?.rule;
  const [f, setF] = useState({
    name: initial?.name || '', description: initial?.description || '', sequence: String(initial?.sequence ?? defaultSequence ?? 1),
    assignment_type: (initial?.assignment_type || '') as AssignmentType | '', objectives: initial?.objectives || '', instructions: initial?.instructions || '',
    rule_name: initial?.rule_name || '',
    completion_policy_id: initial?.completion_policy_id || '', practice_id: initial?.practice_id || '',
  });
  const [useRule, setUseRule] = useState(hadRule);
  const [rule, setRule] = useState<RuleNode | null>(initial?.rule || null);
  const [err, setErr] = useState<string | null>(null);
  const issues = useMemo(() => validateRule(rule), [rule]);
  const ruleOk = !!rule && issues.length === 0;

  const save = () => {
    setErr(null);
    if (!f.name.trim()) return setErr(t('training:track_err_name', 'Give the track a name.'));
    if (useRule && !ruleOk) return setErr(t('training:track_err_rule', 'Finish the track rule or turn it off.'));
    const input: TrackInput = {
      name: f.name.trim(), description: f.description.trim() || undefined, sequence: Number(f.sequence) || undefined,
      assignment_type: f.assignment_type || undefined, objectives: f.objectives.trim() || undefined, instructions: f.instructions.trim() || undefined,
    };
    // rule absent = keep (update); null = remove an existing rule; tree = replace.
    if (useRule) { input.rule = rule; input.rule_name = f.rule_name.trim() || f.name.trim(); }
    else if (hadRule) input.rule = null;
    // Bindings follow the same absent/null/id convention.
    if (f.completion_policy_id) input.completion_policy_id = f.completion_policy_id; else if (initial?.completion_policy_id) input.completion_policy_id = null;
    if (f.practice_id) input.practice_id = f.practice_id; else if (initial?.practice_id) input.practice_id = null;
    onSave(input);
  };

  return (
    <div className={`rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-3 space-y-2 ${className || ''}`} data-testid="track-editor">
      <div className="text-sm font-medium">{initial?.id || initial?.name ? t('training:track_edit', 'Edit track') : t('training:track_add', 'Add track')}</div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2"><Label htmlFor={`${idPrefix}-name`} className="text-xs">{t('training:track_name', 'Track name')}</Label>
          <Input id={`${idPrefix}-name`} className="h-9" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('training:track_name_ph', 'e.g. Drivers · Office staff')} /></div>
        <div className="space-y-1"><Label htmlFor={`${idPrefix}-seq`} className="text-xs">{t('training:track_sequence', 'Order')}</Label>
          <Input id={`${idPrefix}-seq`} className="h-9" type="number" min={1} value={f.sequence} onChange={(e) => setF({ ...f, sequence: e.target.value })} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor={`${idPrefix}-desc`} className="text-xs">{t('training:description', 'Description')}</Label>
          <Input id={`${idPrefix}-desc`} className="h-9" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor={`${idPrefix}-atype`} className="text-xs">{t('training:assignment_type', 'Assignment type')}</Label>
          <select id={`${idPrefix}-atype`} className={selectCls} value={f.assignment_type} onChange={(e) => setF({ ...f, assignment_type: e.target.value as AssignmentType | '' })}>
            <option value="">{t('training:track_inherit_type', 'Same as session')}</option>
            <option value="required">{t('training:required', 'Required')}</option>
            <option value="recommended">{t('training:recommended', 'Recommended')}</option>
            <option value="optional">{t('training:optional', 'Optional')}</option>
          </select></div>
        <div className="space-y-1 sm:col-span-3"><Label htmlFor={`${idPrefix}-obj`} className="text-xs">{t('training:objectives', 'Objectives')}</Label>
          <Textarea id={`${idPrefix}-obj`} rows={2} value={f.objectives} onChange={(e) => setF({ ...f, objectives: e.target.value })} /></div>
        <div className="space-y-1 sm:col-span-3"><Label htmlFor={`${idPrefix}-instr`} className="text-xs">{t('training:track_instructions', 'Instructions for participants')}</Label>
          <Textarea id={`${idPrefix}-instr`} rows={2} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
          <input type="checkbox" className="accent-orange-500" checked={useRule} onChange={(e) => setUseRule(e.target.checked)} disabled={busy} />
          {t('training:track_use_rule', 'Limit this track to people who match a rule')}
        </label>
        <p className="text-[11px] text-muted-foreground">{t('training:track_rule_help', 'A track without a rule is the default for everyone else. The platform assigns each participant to the first track (by order) whose rule matches; pinned people stay where you put them.')}</p>
        {useRule && (
          <div className="space-y-2">
            <div className="space-y-1"><Label htmlFor={`${idPrefix}-rule-name`} className="text-xs">{t('training:rule_name', 'Rule name')}</Label>
              <Input id={`${idPrefix}-rule-name`} className="h-8" value={f.rule_name} onChange={(e) => setF({ ...f, rule_name: e.target.value })} placeholder={f.name.trim() || t('training:rule_name_ph', 'e.g. Finance + HR')} /></div>
            {facetsQ.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('training:loading', 'Loading')}…</div>}
            {facetsQ.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(facetsQ.error, t('training:facets_unavailable', 'The rule builder vocabulary could not be loaded.'))}</p>}
            {facetsQ.data && (
              <ConditionGroupBuilder idPrefix={`${idPrefix}-rule`} facets={vocab.facets} options={vocab.options} people={vocab.people} labels={vocab.labels} value={rule} onChange={setRule} disabled={busy} />
            )}
            <Button size="sm" variant="outline" disabled={!ruleOk || preview.isPending} onClick={() => preview.mutate({ rule })} data-testid={`${idPrefix}-preview`}>
              {preview.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_preview', 'Preview matched employees')}
            </Button>
            {preview.data && <AudiencePreviewPanel preview={preview.data} className="rounded-md border border-border p-2" />}
          </div>
        )}
      </div>
      <PolicyPracticeSelects idPrefix={`${idPrefix}-bind`} policyId={f.completion_policy_id} practiceId={f.practice_id} manageHref={manageHref} disabled={busy}
        emptyLabel={t('training:track_inherit_binding', 'Same as session')}
        onChange={(p) => setF({ ...f, ...(p.policyId !== undefined ? { completion_policy_id: p.policyId } : {}), ...(p.practiceId !== undefined ? { practice_id: p.practiceId } : {}) })} />
      {err && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{err}</div>}
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid={`${idPrefix}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}</Button>
      </div>
    </div>
  );
};

export default TrackEditor;
